<template>
    <div
        selectbox
        question
        :id="`question-${questionData.number}`"
        :ref="`question-${questionData.id}`"
        @keyup.enter="submitQuestion"
        @click="exitSelectbox"
        @keydown.shift.tab="focusOut($event)"
        @keydown.prevent.down.exact="move(1, $event)"
        @keydown.prevent.up.exact="move(-1, $event)"
        v-click-outside="exitSelectbox"
    >
        <questionheader :questionData="questionData" />
        <div data-question-main>
            <div
                data-dropdown
                :data-empty-search="isEmptySearch"
                :data-has-value="answersGiven > 0"
                ref="dropdown"
                :disabled="!get.editingAllowed(questionData) || state.global.showLoader"
            >
                <font-awesome-icon
                    data-search
                    v-if="isEmptySearch"
                    icon="search"
                    aria-hidden="true"
                    id="search-icon"
                />
                <input
                    @focus="showAnswers"
                    :placeholder="placeholder"
                    v-model="searchFilter"
                    ref="search"
                    type="search"
                    :id="`question-${questionData.number}_search-${questionData.number}`"
                    :disabled="!get.editingAllowed(questionData) || state.global.showLoader"
                    @keydown.esc.exact="focusOut($event)"
                    @keydown.shift.tab="focusOut($event)"
                />
                <font-awesome-icon data-icon icon="angle-down" aria-hidden="true" id="angle-icon" />
                <ul
                    role="listbox"
                    :aria-expanded="selectboxIsExpanded"
                    :aria-labelledby="`header-${questionData.number}`"
                    :aria-activedescendant="selectedOptionId"
                    ref="dropdownlist"
                >
                    <li
                        @mousedown="selectAnswer($event, answer)"
                        v-for="(answer, index) in filteredAnswer"
                        ref="select-items"
                        :key="index"
                        :tabindex="0"
                        @keydown.esc.exact="focusOut($event)"
                        @keydown.prevent.space.exact="selectAnswer($event, answer)"
                        @keydown.enter.exact="selectAnswer($event, answer)"
                        @keydown.prevent.tab.exact="move(1, $event)"
                        @keydown.shift.tab="move(-1, $event)"
                        v-html="$sanitize(answer.label)"
                        :id="`question-${questionData.number}_answer-${answer.number}`"
                        role="option"
                    ></li>
                </ul>
            </div>
        </div>

        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import methods from "@/global/methods";

export default {
    name: `selectbox-question`,
    mixins: [mixins.defaultQuestion],
    data() {
        return {
            searchFilter: ``,
            selectboxIsExpanded: false,
        };
    },
    computed: {
        selectItems() {
            return this.$refs[`select-items`];
        },
        answersGiven: function () {
            return this.chosenAnswers.length;
        },
        //Shows different placeholders depending on input
        placeholder: function () {
            let placeholder;

            if (this.selectboxIsExpanded) {
                placeholder = this.get.textResource(`rs_placeholder_search`);
            } else if (this.answersGiven > 0) {
                placeholder = methods.removeHTMLFromString(this.chosenAnswers[0].label);
            } else {
                placeholder = this.questionData.dummyText;
            }

            return placeholder;
        },
        selectedOptionId: function () {
            if (this.answersGiven > 0) {
                return `answer-${this.chosenAnswers[0].id}`;
            } else {
                return ``;
            }
        },
        // The answers that are selected by the user
        chosenAnswers: function () {
            return this.questionData.answers.filter((answer) => answer.isChecked);
        },
        //Returns the answers that include the word that is typed into the search bar
        filteredAnswer() {
            let searchFilter = this.searchFilter.toLowerCase();

            return this.questionData.answers.filter((answer) => {
                let label = answer.label.toLowerCase();
                return label.includes(searchFilter);
            });
        },
        isEmptySearch() {
            return this.searchFilter === ``;
        },
    },
    methods: {
        moveFocus(index) {
            this.selectItems[index]?.focus();
        },
        move(targetItem, event) {
            const index = this.selectItems.findIndex((e) => e === event.target);
            this.moveFocus(index + targetItem);
        },
        focusOut(e) {
            e.target.blur();
            this.selectboxIsExpanded = false;
        },
        selectAnswer(e, answer) {
            this.questionData.answers.forEach((answer) => (answer.isChecked = false));
            answer.isChecked = true;
            this.searchFilter = ``;
            this.selectboxIsExpanded = false;

            // Set(because the focus will be somewhere else) and then lose focus so that the dropdown is closed by the focus-within css pseudo class
            e.target.focus();
            e.target.blur();
        },
        showAnswers() {
            let questionRef = this.$refs[`question-${this.questionData.id}`];
            this.searchFilter = ``;
            this.selectboxIsExpanded = true;

            //check if is android or iphone and then scrolls selectbox to top of screen
            if (this.state.global.isMobile) {
                setTimeout(() => {
                    questionRef.scrollIntoView();
                }, 100);
            }
        },
        // Empty the search when someone leaves the selectbox
        exitSelectbox(e) {
            if (e.srcElement.type !== `search`) {
                this.searchFilter = ``;
                this.selectboxIsExpanded = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./../styles/variables.scss";

[data-answer] {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    width: 100%;
    pointer-events: none;
}

#search-icon {
    display: none;
    position: absolute;
    top: 13px;
    left: 17px;
    font-size: 18px;
}

#angle-icon {
    position: absolute;
    top: 15px;
    right: 18px;
    font-size: 18px;
}

[data-question-main]:focus-within {
    ul {
        display: block;
    }

    #angle-icon {
        display: none;
    }

    #search-icon {
        display: block;
    }

    [data-empty-search="true"] input[type="search"] {
        padding-left: 60px;
    }
}

[data-dropdown] {
    position: relative;
    display: block;
    font-size: calc(var(--AnswerStyling-font-size) * 1px);
    width: 100%;
    max-width: 600px;
    height: 100%;
    pointer-events: all;

    &[data-has-value] {
        input[type="search"] {
            margin-top: -1px;
            border: 2px solid var(--primary-color);
        }

        ul {
            border: 2px solid var(--primary-color);
        }
    }

    svg {
        pointer-events: none;
    }

    input[type="search"] {
        background: #fff;
        cursor: pointer;
        border: 1px solid #333;
        border-radius: 3px;
        color: #454848;
        display: block;
        padding: 11px 40px 11px 13px;
        width: 100%;
        text-overflow: ellipsis;
        transition: none;
        background: #f8f8f8;
        font-size: calc(var(--AnswerStyling-font-size) * 1px);

        &::placeholder {
            color: black;
        }

        &::-moz-placeholder {
            opacity: 1;
            color: black;
        }
    }
    &[disabled] {
        input[type="search"] {
            border-color: #d8d8d8;
            background: #e4e4e4;
            color: #909090;
            cursor: default;
            pointer-events: none;
            &::placeholder {
                color: #909090;
            }
        }
        svg {
            color: #909090;
        }
    }
}

ul {
    background-color: #fff;
    width: 100%;
    max-height: 250px;
    border: 1px solid #333;
    border-radius: 0 0 5px 5px;
    border-top: none;
    box-shadow: 0px -8px 34px 0px rgba(0, 0, 0, 0.05);
    overflow: auto;
    padding: 0;
    z-index: 1;
    padding-top: 2px;
    margin-top: -4px;
    min-width: 300px;
    position: absolute;
    display: none;

    &::-webkit-scrollbar {
        -webkit-appearance: none;
    }

    &::-webkit-scrollbar:vertical {
        width: 12px;
    }

    &::-webkit-scrollbar:horizontal {
        height: 12px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: 10px;
        border: 2px solid #ffffff;
    }

    &::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: #ffffff;
    }

    li {
        color: #454848;
        line-height: 1.5em;
        padding: 8px 20px;
        text-decoration: none;
        width: 100%;
        display: block;
        cursor: pointer;
        font-size: calc(var(--AnswerStyling-font-size) * 1px);

        &:nth-child(odd) {
            background: #f7f7f7;
        }

        &:hover {
            background-color: var(--primary-color);
            color: white;
        }
    }
}

@include media-break(mobile) {
    [question] div[data-question-main] {
        ul {
            max-height: calc(100vh - 150px);
        }

        [data-dropdown] {
            font-size: 1em;

            &[data-open="true"] {
                position: fixed;
                right: 10px;
                left: 10px;
                z-index: 10001;
                animation: openOnMobile 0.2s ease-out forwards;
            }
        }
    }
}

@keyframes openOnMobile {
    from {
        top: 300px;
        width: calc(100% - 20px);
    }
    to {
        top: 10px;
        width: calc(100% - 20px);
    }
}
</style>